import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calculator } from 'lucide-react';
import { Button } from './Button';

type IndustryStart = {
  id: string;
  label: string;
  avgValue: number;
  missedCalls: number;
};

const industries: IndustryStart[] = [
  { id: 'contractor', label: 'Contractor / Trades', avgValue: 850, missedCalls: 8 },
  { id: 'salon', label: 'Salon / Spa', avgValue: 120, missedCalls: 15 },
  { id: 'consultant', label: 'Professional Services', avgValue: 1500, missedCalls: 3 },
  { id: 'retail', label: 'Retail / Restaurant', avgValue: 55, missedCalls: 25 },
  { id: 'other', label: 'Other Business', avgValue: 400, missedCalls: 5 },
];

export const ROICalculator = ({ onBookCall }: { onBookCall?: () => void }) => {
  const [selectedIndustry, setSelectedIndustry] = useState<IndustryStart>(industries[0]);
  const [avgValue, setAvgValue] = useState(industries[0].avgValue);
  const [missedCalls, setMissedCalls] = useState(industries[0].missedCalls);

  const weeklyLoss = avgValue * missedCalls;
  const monthlyLoss = weeklyLoss * 4.3; // Avg weeks in a month
  const annualLoss = weeklyLoss * 52;

  const handleIndustryChange = (industryId: string) => {
    const industry = industries.find(i => i.id === industryId) || industries[0];
    setSelectedIndustry(industry);
    setAvgValue(industry.avgValue);
    setMissedCalls(industry.missedCalls);
  };

  return (
    <section className="py-24 bg-brand-base border-y border-brand-border relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/80 to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left: Headline & Pitch */}
          <div>
             <div className="inline-flex items-center px-4 py-2 rounded-full bg-brand-primary/10 border border-brand-primary/20 mb-6">
                <Calculator className="w-4 h-4 text-brand-primary mr-2" />
                <span className="text-sm text-brand-primary font-medium">Revenue Loss Calculator</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold mb-6 font-display">
              Stop Guessing. <br />
              <span className="text-white">Start Knowing.</span>
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-lg">
              Most business owners underestimate how much missed calls cost them. Use our calculator to see the real opportunity cost of your busy signals.
            </p>
            
            <div className="hidden lg:block">
              <Button variant="accent" size="lg" className="shadow-[0_0_20px_rgba(6,182,212,0.3)]" onClick={onBookCall}>
                Stop Losing Money Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
               <p className="mt-4 text-xs text-slate-500 font-medium uppercase tracking-wide">
                 30-Day Money Back Guarantee
               </p>
            </div>
          </div>

          {/* Right: Calculator Card */}
          <div className="bg-brand-dark border border-brand-border rounded-2xl p-6 md:p-8 shadow-2xl relative">
            {/* Industry Selector */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-slate-400 mb-2">Select Your Industry</label>
              <div className="grid grid-cols-2 gap-2">
                {industries.map((ind) => (
                  <button
                    key={ind.id}
                    onClick={() => handleIndustryChange(ind.id)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-all text-left truncate ${
                      selectedIndustry.id === ind.id 
                      ? 'bg-brand-primary/20 border-brand-primary text-white' 
                      : 'bg-brand-base border-transparent text-slate-400 hover:border-brand-border'
                    }`}
                  >
                    {ind.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sliders */}
            <div className="space-y-6 mb-8">
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium text-slate-300">Avg. Customer Value</label>
                  <span className="text-brand-primary font-bold">${avgValue}</span>
                </div>
                <input 
                  type="range" 
                  min="50" 
                  max="5000" 
                  step="50" 
                  value={avgValue}
                  onChange={(e) => setAvgValue(Number(e.target.value))}
                  className="w-full h-2 bg-brand-base rounded-lg appearance-none cursor-pointer accent-brand-primary"
                />
              </div>

               <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium text-slate-300">Missed Opportunities / Week</label>
                  <span className="text-brand-primary font-bold">{missedCalls} calls</span>
                </div>
                <input 
                  type="range" 
                  min="1" 
                  max="100" 
                  step="1" 
                  value={missedCalls}
                  onChange={(e) => setMissedCalls(Number(e.target.value))}
                   className="w-full h-2 bg-brand-base rounded-lg appearance-none cursor-pointer accent-brand-primary"
                />
              </div>
            </div>

            {/* Results */}
            <div className="bg-brand-base/50 rounded-xl p-6 border border-brand-border text-center">
              <p className="text-sm text-slate-400 font-medium mb-1 uppercase tracking-wider">You Could Be Losing Up To</p>
               <motion.div 
                key={monthlyLoss}
                initial={{ scale: 0.9, opacity: 0.5 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-4xl md:text-5xl font-extrabold text-red-500 font-display mb-2"
              >
                ${Math.floor(monthlyLoss).toLocaleString()}
              </motion.div>
              <p className="text-sm text-slate-400 mb-4">per month in revenue</p>
              
              <div className="pt-4 border-t border-brand-border/50 flex justify-between items-center text-sm">
                <span className="text-slate-500">Annual Impact:</span>
                <span className="text-xl font-bold text-white">${Math.floor(annualLoss).toLocaleString()}</span>
              </div>
            </div>
            
             <div className="mt-6 lg:hidden">
              <Button variant="accent" size="lg" className="w-full shadow-[0_0_20px_rgba(6,182,212,0.3)]" onClick={onBookCall}>
                Capture This Revenue
              </Button>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};
